import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "../../Layout/index.jsx";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button, Modal } from "flowbite-react";
import {
  useGetAllAssignmentQuestionQuery,
  useGetAssignmentByCampusQuery,
} from "../../../../graphql/generated.tsx";
import { FaCalendarAlt } from "react-icons/fa";
import { AiOutlineClockCircle, AiOutlineQuestionCircle } from "react-icons/ai";
import { LuMousePointerClick } from "react-icons/lu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Loader from "../../../../components/Loader.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";
import ReactSelect from "react-select";

const assignmentFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
    "&:hover": {
      borderColor: "#9ca3af",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 10px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#d1d5db"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    color: "#111827",
    ":active": {
      backgroundColor: "#d1d5db",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 40,
  }),
};

const ACCENTS = [
  { bar: "#8A5A2B", pillBg: "#F3E8DA", pillText: "#8A5A2B", pillBgHover: "#EAD9C3" },
  { bar: "#0F6E56", pillBg: "#E1F5EE", pillText: "#0F6E56", pillBgHover: "#C9EADF" },
  { bar: "#3C3489", pillBg: "#EEEDFE", pillText: "#3C3489", pillBgHover: "#E0DFFE" },
];

const getRowAccent = (globalIndex, rowSize = 3) => {
  const rowIndex = Math.floor(globalIndex / rowSize);
  const indexInRow = globalIndex % rowSize;
  const permutations = [
    [0, 1, 2],
    [1, 2, 0],
    [2, 0, 1],
    [0, 2, 1],
    [2, 1, 0],
    [1, 0, 2],
  ];
  const order = permutations[rowIndex % permutations.length];
  return ACCENTS[order[indexInRow] ?? 0];
};

const CalSVG = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="10" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M4.5 1.5v2M8.5 1.5v2M1.5 5.5h10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowSVG = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4.5M9.5 2.5V7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const cardStyle = {
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #e5e7eb",
  boxSizing: "border-box",
  fontFamily: "Poppins, sans-serif",
};

const AssignmentCard = ({ assignment, campusName, globalIndex, dataTour }) => {
  const accent = getRowAccent(globalIndex, 3);
  const mildTopBar = `${accent.bar}99`;
  const [isHovered, setIsHovered] = useState(false);
  const { data: assignmentQuestionsData } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId: assignment?.id || "", page: 1, limit: 1000 },
    skip: !assignment?.id,
  });

  const questionsCount = assignmentQuestionsData?.getAllAssignmentQuestion?.length ?? null;
  const formatDurationLabel = (durationValue) => {
    if (durationValue === null || durationValue === undefined || durationValue === "") {
      return "-";
    }

    const raw = String(durationValue).trim();
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return "-";

    // Support inputs like "1.30" as 1 hr 30 min (HH.MM style only when exactly 2 minute digits).
    const [hoursPart, minutesPartRaw] = raw.split(".");
    if (minutesPartRaw !== undefined) {
      const parsedHours = Number.parseInt(hoursPart, 10) || 0;
      const parsedMinutes = Number.parseInt(minutesPartRaw, 10);

      if (
        minutesPartRaw.length === 2 &&
        Number.isFinite(parsedMinutes) &&
        parsedMinutes >= 0 &&
        parsedMinutes <= 59
      ) {
        if (parsedMinutes === 0) {
          return `${parsedHours} hr`;
        }
        return `${parsedHours} hr ${parsedMinutes} min`;
      }
    }

    // Fallback: treat duration as decimal hours.
    const totalMinutes = Math.round(numeric * 60);
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    if (mins === 0) return `${hrs} hr`;
    return `${hrs} hr ${mins} min`;
  };

  const durationLabel = formatDurationLabel(assignment?.duration);

  const dynamicCardStyle = {
    ...cardStyle,
    transform: "translateY(0) scale(1)",
    boxShadow: "0 12px 30px -18px rgba(15, 23, 42, 0.2)",
    transition: "box-shadow 0.22s ease",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
  };

  const arrowContainerStyle = {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: isHovered ? accent.pillBgHover : "#F9FAFB",
    border: `1px solid ${isHovered ? accent.bar : "#EAECF0"}`,
    color: isHovered ? accent.bar : "#667085",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transform: isHovered ? "scale(1.12)" : "scale(1)",
    boxShadow: isHovered ? `0 6px 14px -8px ${accent.bar}` : "none",
    transition: "background 0.2s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
  };

  return (
    <Link
      to={`/admin/reports/assignments/${campusName}/${assignment.id}`}
      className="font-poppins"
      style={dynamicCardStyle}
      data-tour={dataTour}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ height: 7, background: mildTopBar, width: "100%", flexShrink: 0 }} />

      <div style={{ padding: "20px 18px 16px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <p
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: "#101828",
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {assignment.name}
          </p>
        </div>

        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: "#5B6475",
            lineHeight: 1.7,
            flex: 1,
            fontWeight: 500,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {assignment.short_description || "Take this assessment based on what you know and demonstrate your understanding."}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", padding: "4px 0", marginTop: 6 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <AiOutlineClockCircle size={16} color="#64748B" />
              {durationLabel}
            </div>
            <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <AiOutlineQuestionCircle size={16} color="#64748B" />
              {questionsCount ?? "-"} questions
            </div>
            <div style={{ width: 1, height: 16, background: "#E2E8F0" }} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 600, color: "#0F172A" }}>
              <CalSVG />
              {assignment.due_date
                ? new Date(assignment.due_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                : "No due date"}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "12px 18px 16px",
          display: "flex",
          alignItems: "center",
          borderTop: "1px solid #F2F4F7",
          gap: 10,
          background: "linear-gradient(90deg, rgba(248,250,252,0.9), rgba(255,255,255,0.6))",
        }}
      >
        {assignment.domain ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "3px 9px",
              borderRadius: 8,
              letterSpacing: "0.03em",
              whiteSpace: "nowrap",
              background: accent.pillBg,
              color: accent.pillText,
            }}
          >
            {assignment.domain.toUpperCase()}
          </span>
        ) : (
          <span />
        )}
        <div style={{ ...arrowContainerStyle, marginLeft: "auto" }}>
          <ArrowSVG />
        </div>
      </div>
    </Link>
  );
};

const AssignmentListing = () => {
  const { campusName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assignments, setAssignments] = useState([]); // raw data from API
  const [filteredAssignments, setFilteredAssignments] = useState([]); // assignments displayed
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [currentPage, setCurrentPage] = useState(() => {
    const page = Number(searchParams.get("page") || 1);
    return Number.isFinite(page) && page > 0 ? page : 1;
  });
  const itemsPerPage = 9;

  // Date filter states
  const [dateFilter, setDateFilter] = useState(() => searchParams.get("filter") || "last15");
  const [fromDate, setFromDate] = useState(() => searchParams.get("from") || "");
  const [toDate, setToDate] = useState(() => searchParams.get("to") || "");
  const [customDateRange, setCustomDateRange] = useState(() => ({
    startDate: searchParams.get("from") ? new Date(searchParams.get("from")) : null,
    endDate: searchParams.get("to") ? new Date(searchParams.get("to")) : null,
  }));
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const[ Isload, setIsload] = useState(true);
  const hasMountedRef = useRef(false);
  // Custom date range stored as Date objects (used by filter)

  // Modal popup state
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Date filter options configuration
  const DATE_FILTER_OPTIONS = [
    { value: "last15", label: "Last 15 Days" },
    { value: "last30", label: "Last 30 Days" },
    { value: "last3months", label: "Last 3 Months" },
    { value: "custom", label: "Custom Dates" }
  ];
  

  useEffect(() => {
    const hasVisited = localStorage.getItem("assignmentFilterVisited");

    if (!hasVisited) {
      setShowFilterModal(true); 
      localStorage.setItem("assignmentFilterVisited", "true"); // mark visited
    } else {
      setShowFilterModal(false);
    }
  }, []);

  
  useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);



  const { data, loading } = useGetAssignmentByCampusQuery({
    variables: { campusName },
  });

  // Set raw assignments from API and sort by due date (latest first)
  useEffect(() => {
    if (data?.getAssignmentByCampus) {
      const sorted = [...data.getAssignmentByCampus].sort(
        (assignmentA, assignmentB) => new Date(assignmentB.due_date) - new Date(assignmentA.due_date)
      );
      setAssignments(sorted);
      setFilteredAssignments([]); // keep empty until Apply Filter
    }
  }, [data]);

  const applyFilter = () => {
    if (dateFilter === "custom") {
      
      const start = fromDate ? new Date(fromDate) : customDateRange.startDate;
      const end = toDate ? new Date(toDate) : customDateRange.endDate;
      setCustomDateRange({
        startDate: start || null,
        endDate: end || null,
      });
    } else {

      setCustomDateRange({ startDate: null, endDate: null });
      setFromDate("");
      setToDate("");
    }
    setShowFilterModal(false);
  };

 
  useEffect(() => {
    let filtered = [...assignments];
    const today = new Date();

    if (dateFilter === "last15") {
      const last15 = new Date();
      last15.setDate(today.getDate() - 15);
      filtered = filtered.filter((assignment) => new Date(assignment.due_date) >= last15);
    } else if (dateFilter === "last30") {
      const last30 = new Date();
      last30.setDate(today.getDate() - 30);
      filtered = filtered.filter((assignment) => new Date(assignment.due_date) >= last30);
    } else if (dateFilter === "last3months") {
      const last3Months = new Date();
      last3Months.setMonth(today.getMonth() - 3);
      filtered = filtered.filter((assignment) => new Date(assignment.due_date) >= last3Months);
    } else if (
      dateFilter === "custom" &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      const from = new Date(customDateRange.startDate);
      const to = new Date(customDateRange.endDate);
      to.setHours(23, 59, 59, 999); // Include all of the end date
      filtered = filtered.filter(
        (assignment) => new Date(assignment.due_date) >= from && new Date(assignment.due_date) <= to
      );
    }

    // Apply search filter (safe-guard if fields are undefined)
    filtered = filtered.filter((assignment) => {
      const name = (assignment.name || "").toString().toLowerCase();
      const domain = (assignment.domain || "").toString().toLowerCase();
      const query = search.toLowerCase();
      return name.includes(query) || domain.includes(query);
    });

    // Sort filtered assignments
    filtered.sort((assignmentA, assignmentB) => new Date(assignmentB.due_date) - new Date(assignmentA.due_date));

    setFilteredAssignments(filtered);
  }, [search, dateFilter, customDateRange, assignments]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [search, dateFilter, customDateRange]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    ["filter", "from", "to", "q", "page"].forEach((k) => nextParams.delete(k));

    if (dateFilter) nextParams.set("filter", dateFilter);
    if (fromDate) nextParams.set("from", fromDate);
    if (toDate) nextParams.set("to", toDate);
    if (search.trim()) nextParams.set("q", search.trim());
    if (currentPage > 1) nextParams.set("page", String(currentPage));

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [dateFilter, fromDate, toDate, search, currentPage, searchParams, setSearchParams]);

  // Compute date display strings for cleaner JSX
  const startDateDisplay = customDateRange.startDate
    ? new Date(customDateRange.startDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Start date";

  const endDateDisplay = customDateRange.endDate
    ? new Date(customDateRange.endDate).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "End date";

  // Handler functions for date picker interactions
  const handleStartDateButtonClick = () => {
    if (activeField === "start") {
      setShowCalendar((prevState) => !prevState);
    } else {
      setActiveField("start");
      setShowCalendar(true);
    }
  };

  const handleStartDateChange = (date) => {
    setCustomDateRange((prev) => ({ ...prev, startDate: date || null }));
    setFromDate(date ? date.toISOString().split("T")[0] : "");
    if (date) setShowCalendar(false);
  };

  const handleEndDateButtonClick = () => {
    if (activeField === "end") {
      setShowCalendar((prevState) => !prevState);
    } else {
      setActiveField("end");
      setShowCalendar(true);
    }
  };

  const handleEndDateChange = (date) => {
    setCustomDateRange((prev) => ({ ...prev, endDate: date || null }));
    setToDate(date ? date.toISOString().split("T")[0] : "");
    if (date) setShowCalendar(false);
  };

  const handleCloseCalendar = () => {
    setShowCalendar(false);
  };

  return (
    <AdminLayout>
      <NetworkIndicator />
      {Isload ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      ) : (
      <div className="max-w-full sm:p-0 md:p-0 font-poppins">
        {/* Filter Modal */}
        <Modal
          show={showFilterModal}
          size="md"
          onClose={() => setShowFilterModal(false)}
        >
          <Modal.Header>
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              Select Date Filter
            </span>
          </Modal.Header>

          <Modal.Body className="max-h-[400px] overflow-y-auto">
            <div className="flex flex-col gap-4">
              <ReactSelect
                isSearchable={false}
                options={DATE_FILTER_OPTIONS}
                value={DATE_FILTER_OPTIONS.find((option) => option.value === dateFilter) || DATE_FILTER_OPTIONS[0]}
                onChange={(option) => setDateFilter(option?.value || "last15")}
                styles={assignmentFilterSelectStyles}
              />

              {dateFilter === "custom" && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="text-xs text-gray-500">
                    (Or use the calendar controls on the page)
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={applyFilter}>Apply Filter</Button>
          </Modal.Footer>
        </Modal>

        <div className="max-w-full font-poppins">
          <div className="max-w-full px-6 pt-3 pb-3 bg-white border border-b-0 border-gray-200 shadow-md sm:px-6 rounded-t-xl">
            <div className="relative w-full sm:w-[760px] max-w-full overflow-hidden rounded-xl border border-sky-300 bg-sky-100 px-5 py-4 shadow-sm min-h-[96px]">
              <div className="absolute inset-y-0 left-0 w-1 bg-sky-900" />
              <div className="flex items-center gap-2.5 pl-2">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 bg-white border border-sky-200 rounded-full shadow-sm">
                  <LuMousePointerClick className="text-sky-900" size={18} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold leading-tight text-slate-800 sm:text-lg">
                    View Assignment Report
                  </h3>
                  <p className="mt-1 text-sm font-medium text-slate-700 sm:text-base">
                    Click on any assignment card to view submissions and scores.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-full p-4 mb-0 bg-white border border-t-0 border-b-0 border-gray-200 shadow-md sm:p-6">
            <div className="flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-end sm:gap-4">
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-3">
                <div className="w-full sm:w-48">
                  <p className="mb-1 text-sm font-semibold text-gray-600">Date filter</p>
                  <ReactSelect
                    isSearchable={false}
                    options={DATE_FILTER_OPTIONS}
                    value={DATE_FILTER_OPTIONS.find((option) => option.value === dateFilter) || DATE_FILTER_OPTIONS[0]}
                    onChange={(option) => setDateFilter(option?.value || "last15")}
                    styles={assignmentFilterSelectStyles}
                  />
                </div>

                {dateFilter === "custom" && (
                  <div className="flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={handleStartDateButtonClick}
                        className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-lg shadow-sm focus:outline-none transition-all
                          ${customDateRange.startDate ? "border-blue-500" : "border-gray-300"}
                          hover:shadow-md focus:ring-2 focus:ring-blue-500`}
                      >
                        <FaCalendarAlt className="text-gray-500" />
                        <span
                          className={`${
                            customDateRange.startDate ? "text-gray-800" : "text-gray-400"
                          }`}
                        >
                          {startDateDisplay}
                        </span>
                      </button>
                      {showCalendar && activeField === "start" && (
                        <div className="absolute left-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Select start date
                            </span>
                            <button
                              onClick={handleCloseCalendar}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                          <DatePicker
                            selected={customDateRange.startDate}
                            onChange={handleStartDateChange}
                            inline
                            calendarClassName="bt-calendar"
                          />
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <button
                        type="button"
                        onClick={handleEndDateButtonClick}
                        className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-lg shadow-sm focus:outline-none transition-all
                          ${customDateRange.endDate ? "border-blue-500" : "border-gray-300"}
                          hover:shadow-md focus:ring-2 focus:ring-blue-500`}
                      >
                        <FaCalendarAlt className="text-gray-500" />
                        <span
                          className={`${
                            customDateRange.endDate ? "text-gray-800" : "text-gray-400"
                          }`}
                        >
                          {endDateDisplay}
                        </span>
                      </button>
                      {showCalendar && activeField === "end" && (
                        <div className="absolute left-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-semibold text-gray-700">
                              Select end date
                            </span>
                            <button
                              onClick={handleCloseCalendar}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          </div>
                          <DatePicker
                            selected={customDateRange.endDate}
                            onChange={handleEndDateChange}
                            inline
                            calendarClassName="bt-calendar"
                            minDate={customDateRange.startDate}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search by name or domain"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="max-w-full min-h-screen p-4 pb-10 bg-white border border-t-0 border-gray-200 shadow-md sm:p-6 sm:pb-12 rounded-b-xl">
            <div>
              {loading ? (
                <div className="flex items-center justify-center h-[60vh]">
                  <div className="w-12 h-12 border-4 border-blue-400 rounded-full border-t-transparent animate-spin"></div>
                </div>
              ) : filteredAssignments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[60vh] text-gray-600">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/6134/6134065.png"
                    alt="No data"
                    className="w-20 h-20 mb-4 opacity-60"
                  />
                  <p>No assignments found.</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedAssignments = filteredAssignments.slice(startIndex, endIndex);
                    
                    return (
                      <>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {paginatedAssignments.map((assignment, index) => {
                            const globalIndex = (currentPage - 1) * itemsPerPage + index;
                            return (
                              <AssignmentCard
                                key={assignment.id}
                                assignment={assignment}
                                campusName={campusName}
                                globalIndex={globalIndex}
                                dataTour={index === 0 ? "assignment-card" : ""}
                              />
                            );
                          })}
                        </div>
                        
                        {totalPages > 1 && (
                          <div className="mt-6">
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={setCurrentPage}
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      )}
    </AdminLayout>
  );
};

export default AssignmentListing;
